import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { constant } from "common/constant";
import { putApi } from "common/services/api";
import { getApi, postApi } from "common/services/api";
import { API_PATHS } from "common/services/apiPaths";
import { toast } from "react-toastify";
// import S3Helper from "common/utils/s3-helper";
// import { getS3Config, validateS3Config } from "common/utils/s3configuration";

import S3Helper from "common/utils/s3-helper";
import { getS3Config, validateS3Config } from "common/utils/s3Configuration";
import { deleteManyApi } from "common/services/api";

// Helper function to extract S3 key from URL
const extractS3KeyFromUrl = (url) => {
  if (!url) return null;
  try {
    const urlObj = new URL(url);
    console.log("s3 key url", urlObj.pathname.substring(1))
    return urlObj.pathname.substring(1); // Remove leading slash
  } catch (error) {
    console.error('Invalid URL:', url);
    return null;
  }
};

// NEW: Delete job profile with attachment cleanup
export const deleteJobProfile = createAsyncThunk(
  "recruitment/deleteJobProfile",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      // 1. Get the job profile data to find attachments
      const jobProfileResponse = await getApi(`${API_PATHS.RECRUITMENT_PROFILES}${id}`)
      const jobProfile = jobProfileResponse.data

      // 2. Extract attachment URLs if they exist
      const attachmentsToDelete = []

      if (jobProfile.jobSummaryAttachment) {
        const jobSummaryKey = extractS3KeyFromUrl(jobProfile.jobSummaryAttachment)
        if (jobSummaryKey) attachmentsToDelete.push(jobSummaryKey)
      }

      if (jobProfile.othersAttachment) {
        const othersKey = extractS3KeyFromUrl(jobProfile.othersAttachment)
        if (othersKey) attachmentsToDelete.push(othersKey)
      }

      // 3. Delete attachments from S3 if any exist
      if (attachmentsToDelete.length > 0) {
        try {
          const s3Config = getS3Config()
          // validateS3Config(s3Config);
          const s3Helper = new S3Helper(s3Config)

          await s3Helper.deleteMultipleFiles(attachmentsToDelete)
          console.log("Successfully deleted attachments:", attachmentsToDelete)
        } catch (s3Error) {
          console.warn("Failed to delete attachments from S3:", s3Error)
          // Continue with job profile deletion even if S3 deletion fails
        }
      }

      // 4. Delete the job profile from the database
      const deleteResponse = await deleteManyApi(`${API_PATHS.POST_RECRUITMENT_PROFILES}/${id}`)

      if (deleteResponse.status === 200) {
        toast.success(constant.jobOpeningDeletedSuccess)
        return id
      } else {
        toast.error(constant.somethingWentWrong)
        return rejectWithValue("Failed to delete job profile")
      }
    } catch (error) {
      console.error("Delete Job Profile Error:", error)
      toast.error(constant.somethingWentWrong)
      return rejectWithValue(error.message || constant.apiReqFailed)
    }
  },
)

// NEW: Delete multiple job profiles with attachment cleanup
export const deleteMultipleJobProfiles = createAsyncThunk(
  "recruitment/deleteMultipleJobProfiles",
  async (ids, { rejectWithValue, dispatch }) => {
    try {
      // 1. Get all job profiles data to find attachments
      const attachmentsToDelete = []

      // Fetch each job profile to get attachment URLs
      for (const id of ids) {
        try {
          const jobProfileResponse = await getApi(`${API_PATHS.RECRUITMENT_PROFILES}${id}`)
          const jobProfile = jobProfileResponse.data

          if (jobProfile.jobSummaryAttachment) {
            const jobSummaryKey = extractS3KeyFromUrl(jobProfile.jobSummaryAttachment)
            if (jobSummaryKey) attachmentsToDelete.push(jobSummaryKey)
          }

          if (jobProfile.othersAttachment) {
            const othersKey = extractS3KeyFromUrl(jobProfile.othersAttachment)
            if (othersKey) attachmentsToDelete.push(othersKey)
          }
        } catch (fetchError) {
          console.log(`test Failed to fetch job profile ${id}:`, fetchError)
          // Continue with other profiles even if one fetch fails
        }
      }

      // 2. Delete attachments from S3 if any exist
      if (attachmentsToDelete.length > 0) {
        try {
          const s3Config = getS3Config()
          // validateS3Config(s3Config);
          const s3Helper = new S3Helper(s3Config)

          await s3Helper.deleteMultipleFiles(attachmentsToDelete)
          console.log("test Successfully deleted attachments:", attachmentsToDelete)
        } catch (s3Error) {
          console.log("Failed to delete attachments from S3:", s3Error)
          // Continue with job profile deletion even if S3 deletion fails
        }
      }

      // 3. Delete the job profiles from the database
      const deleteResponse = await deleteManyApi("api/recruitment/jobOpening/deleteMany", ids)

      if (deleteResponse.status === 200) {
        // const count = ids.length
        // toast.success(`${count} job ${count > 1 ? "openings" : "opening"} deleted successfully`)
        return ids
      } else {

        toast.error(deleteResponse?.response?.data?.message ?? constant.somethingWentWrong)
        return rejectWithValue("test Failed to delete job profiles")
      }
    } catch (error) {

      toast.error(error?.response?.data?.message ?? constant.somethingWentWrong)
      return rejectWithValue(error.message || constant.apiReqFailed)
    }
  },
)

// Existing thunks...
export const fetchRecruitmentData = createAsyncThunk(
  "recruitment/fetchRecruitmentData",
  async (_, { rejectWithValue }) => {
    try {
      const result = await getApi(API_PATHS.RECRUITMENT_PROFILES);
      const userResult = await getApi(API_PATHS.USER);

      if (result.data.length > 0) {
        const updatedData = result.data.map((res) => ({
          ...res,
          hiringManager: userResult.data.user.find((data) => data._id === res.hiringManager)?.username || constant.notAllowed,
          assignedRecruiter: userResult.data.user.find((data) => data._id === res.assignedRecruiter)?.username || constant.notAllowed,
          department: res.department?.name || constant.notAllowed,
        }));

        return updatedData;
      }
      return [];
    } catch (error) {
      return rejectWithValue(constant.somethingWentWrong);
    }
  }
);

export const addRecruitmentProfile = createAsyncThunk(
  "recruitment/addRecruitmentProfile",
  async ({ values, resetForm, navigate }, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState();
      const userId = state.user.user?._id;

      const hasJobSummaryAttachment = values.jobSummaryAttachment && values.jobSummaryAttachment instanceof File;
      const hasOthersAttachment = values.othersAttachment && values.othersAttachment instanceof File;

      if (!hasJobSummaryAttachment && !hasOthersAttachment) {
        const response = await postApi(API_PATHS.POST_RECRUITMENT_PROFILES, values);

        if (response.status === 201) {
          resetForm();
          toast.success(constant.jobOpeningCreatedSuccess);
          dispatch(fetchRecruitmentData());
          navigate("/recruitment/jobProfile");
          return response.data;
        } else {
          toast.error(constant.somethingWentWrong);
          return rejectWithValue(constant.failedToAddProfile);
        }
      }

      const valuesWithoutAttachments = { ...values };
      delete valuesWithoutAttachments.jobSummaryAttachment;
      delete valuesWithoutAttachments.othersAttachment;

      const response = await postApi(API_PATHS.POST_RECRUITMENT_PROFILES, valuesWithoutAttachments);

      if (response.status !== 201) {
        toast.error(constant.somethingWentWrong);
        return rejectWithValue(constant.failedToAddProfile);
      }

      const jobId = response.data._id;
      if (!jobId) {
        toast.error(constant.somethingWentWrong);
        return rejectWithValue("Job ID not found in response");
      }

      const s3Config = getS3Config();
      // validateS3Config(s3Config);;
      const s3Helper = new S3Helper(s3Config);

      const attachmentPaths = {};

      try {
        if (hasJobSummaryAttachment) {
          const jobSummaryResult = await s3Helper.uploadFile(
            values.jobSummaryAttachment,
            {
              keyPrefix: `/job-opening/${jobId}/user/${userId}/`,
              generateUniqueKey: true,
              metadata: {
                jobId: jobId,
                type: 'job-summary'
              }
            }
          );

          if (jobSummaryResult.success) {
            attachmentPaths.jobSummaryAttachment = jobSummaryResult.url;
          } else {
            throw new Error('Failed to upload job summary attachment');
          }
        }

        if (hasOthersAttachment) {
          const otherResult = await s3Helper.uploadFile(
            values.othersAttachment,
            {
              keyPrefix: `/others-attachment/${jobId}/user/${userId}/`,
              generateUniqueKey: true,
              metadata: {
                jobId: jobId,
                type: 'other-attachment'
              }
            }
          );

          if (otherResult.success) {
            attachmentPaths.othersAttachment = otherResult.url;
          } else {
            throw new Error('Failed to upload other attachment');
          }
        }

        const updateResponse = await putApi(
          `${API_PATHS.POST_RECRUITMENT_PROFILES}/${jobId}`,
          attachmentPaths
        );

        if (updateResponse.status === 200) {
          resetForm();
          toast.success(constant.jobOpeningCreatedSuccess);
          dispatch(fetchRecruitmentData());
          navigate("/recruitment/jobProfile");
          return { ...response.data, ...attachmentPaths };
        } else {
          toast.error(updateResponse?.response?.data?.message ?? constant.somethingWentWrong);
          return rejectWithValue("Failed to update job profile with attachment paths");
        }

      } catch (uploadError) {
        console.error('S3 Upload Error:', uploadError);
        toast.error(uploadError?.response?.data?.message ?? "Failed to upload attachments");
        return rejectWithValue(`Upload failed: ${uploadError.message}`);
      }

    } catch (error) {
      console.error('Add Recruitment Profile Error:', error);
      toast.error(error?.response?.data?.message ?? constant.somethingWentWrong);
      return rejectWithValue(error.message || constant.apiReqFailed);
    }
  }
);

// NEW: Update recruitment profile with attachment handling
// export const updateRecruitmentProfile = createAsyncThunk(
//     "recruitment/updateRecruitmentProfile",
//     async ({ id, values, resetForm, navigate }, { rejectWithValue, dispatch, getState }) => {
//       try {
//         const state = getState();
//         const userId = state.user.user?._id;

//         const hasNewJobSummaryAttachment = values.jobSummaryAttachment && values.jobSummaryAttachment instanceof File;
//         const hasNewOthersAttachment = values.othersAttachment && values.othersAttachment instanceof File;

//         // Get current job data to check for existing attachments
//         const currentJobResponse = await getApi(`${API_PATHS.RECRUITMENT_PROFILES}/${id}`);
//         const currentJob = currentJobResponse.data;

//         const s3Config = getS3Config();
//         // validateS3Config(s3Config);;
//         const s3Helper = new S3Helper(s3Config);

//         const attachmentPaths = {};
//         const filesToDelete = [];

//         try {
//           // Handle job summary attachment
//           if (hasNewJobSummaryAttachment) {
//             // Delete old file if exists
//             if (currentJob.jobSummaryAttachment) {
//               const oldKey = extractS3KeyFromUrl(currentJob.jobSummaryAttachment);
//               if (oldKey) {
//                 filesToDelete.push(oldKey);
//               }
//             }

//             // Upload new file
//             const jobSummaryResult = await s3Helper.uploadFile(
//               values.jobSummaryAttachment,
//               {
//                 keyPrefix: `/job-opening/${id}/user/${userId}/`,
//                 generateUniqueKey: true,
//                 metadata: {
//                   jobId: id,
//                   type: 'job-summary'
//                 }
//               }
//             );

//             if (jobSummaryResult.success) {
//               attachmentPaths.jobSummaryAttachment = jobSummaryResult.url;
//             } else {
//               throw new Error('Failed to upload job summary attachment');
//             }
//           }

//           // Handle other attachment
//           if (hasNewOthersAttachment) {
//             // Delete old file if exists
//             if (currentJob.othersAttachment) {
//               const oldKey = extractS3KeyFromUrl(currentJob.othersAttachment);
//               if (oldKey) {
//                 filesToDelete.push(oldKey);
//               }
//             }

//             // Upload new file
//             const otherResult = await s3Helper.uploadFile(
//               values.othersAttachment,
//               {
//                 keyPrefix: `/others-attachment/${id}/user/${userId}/`,
//                 generateUniqueKey: true,
//                 metadata: {
//                   jobId: id,
//                   type: 'other-attachment'
//                 }
//               }
//             );

//             if (otherResult.success) {
//               attachmentPaths.othersAttachment = otherResult.url;
//             } else {
//               throw new Error('Failed to upload other attachment');
//             }
//           }

//           // Delete old files from S3
//           if (filesToDelete.length > 0) {
//             try {
//               await s3Helper.deleteMultipleFiles(filesToDelete);
//             } catch (deleteError) {
//               console.warn('Failed to delete old files:', deleteError);
//               // Don't fail the entire operation if old file deletion fails
//             }
//           }

//           // Prepare update data
//           const updateData = { ...values };
//           delete updateData.jobSummaryAttachment;
//           delete updateData.othersAttachment;

//           // Add new attachment URLs if any
//           Object.assign(updateData, attachmentPaths);

//           // Update job profile
//           const updateResponse = await putApi(
//             `${API_PATHS.POST_RECRUITMENT_PROFILES}/${id}`,
//             updateData
//           );

//           if (updateResponse.status === 200) {
//             if (resetForm) resetForm();
//             toast.success("Job opening updated successfully");
//             dispatch(fetchRecruitmentData()); 
//             if (navigate) navigate("/recruit/jobProfile");
//             return { ...updateResponse.data, ...attachmentPaths };
//           } else {
//             toast.error(constant.somethingWentWrong);
//             return rejectWithValue("Failed to update job profile");
//           }

//         } catch (uploadError) {
//           console.error('S3 Upload Error:', uploadError);
//           toast.error("Failed to upload attachments");
//           return rejectWithValue(`Upload failed: ${uploadError.message}`);
//         }

//       } catch (error) {
//         console.error('Update Recruitment Profile Error:', error);
//         toast.error(constant.somethingWentWrong);
//         return rejectWithValue(error.message || constant.apiReqFailed);
//       }
//     }
// );

// export const updateRecruitmentProfile = createAsyncThunk(
//   "recruitment/updateRecruitmentProfile",
//   async ({ id, values, resetForm, navigate }, { rejectWithValue, dispatch, getState }) => {
//     try {
//       const state = getState();
//       const userId = state.user.user?._id;

//       const s3Config = getS3Config();
//       // validateS3Config(s3Config);;
//       const s3Helper = new S3Helper(s3Config);

//       const hasNewJobSummaryAttachment =
//         values.jobSummaryAttachment && values.jobSummaryAttachment instanceof File;
//       const hasNewOthersAttachment =
//         values.othersAttachment && values.othersAttachment instanceof File;

//       const currentJobResponse = await getApi(`${API_PATHS.RECRUITMENT_PROFILES}${id}`);
//       const currentJob = currentJobResponse.data;

//       const attachmentPaths = {};
//       const filesToDelete = [];

//       // ===== Handle Job Summary Attachment =====
//       if (hasNewJobSummaryAttachment) {
//         const newFile = values.jobSummaryAttachment;

//         if (currentJob.jobSummaryAttachment) {
//           const oldKey = extractS3KeyFromUrl(currentJob.jobSummaryAttachment);
//           if (oldKey) filesToDelete.push(oldKey);
//         }

//         const uploadResult = await s3Helper.uploadFile(newFile, {
//           keyPrefix: `/job-opening/${id}/user/${userId}/`,
//           generateUniqueKey: true,
//           metadata: { jobId: id, type: "job-summary" },
//         });

//         if (uploadResult.success) {
//           attachmentPaths.jobSummaryAttachment = uploadResult.url;
//         } else {
//           throw new Error("Failed to upload job summary attachment");
//         }
//       } else if (
//         !values.jobSummaryAttachment &&
//         currentJob.jobSummaryAttachment
//       ) {
//         // File cleared by user
//         const oldKey = extractS3KeyFromUrl(currentJob.jobSummaryAttachment);
//         if (oldKey) filesToDelete.push(oldKey);
//         attachmentPaths.jobSummaryAttachment = "";
//       }

//       // ===== Handle Others Attachment =====
//       if (hasNewOthersAttachment) {
//         const newFile = values.othersAttachment;

//         if (currentJob.othersAttachment) {
//           const oldKey = extractS3KeyFromUrl(currentJob.othersAttachment);
//           if (oldKey) filesToDelete.push(oldKey);
//         }

//         const uploadResult = await s3Helper.uploadFile(newFile, {
//           keyPrefix: `/others-attachment/${id}/user/${userId}/`,
//           generateUniqueKey: true,
//           metadata: { jobId: id, type: "other-attachment" },
//         });

//         if (uploadResult.success) {
//           attachmentPaths.othersAttachment = uploadResult.url;
//         } else {
//           throw new Error("Failed to upload other attachment");
//         }
//       } else if (
//         !values.othersAttachment &&
//         currentJob.othersAttachment
//       ) {
//         // File cleared by user
//         const oldKey = extractS3KeyFromUrl(currentJob.othersAttachment);
//         if (oldKey) filesToDelete.push(oldKey);
//         attachmentPaths.othersAttachment = "";
//       }

//       // ===== Delete Old Files from S3 if any =====
//       if (filesToDelete.length > 0) {
//         try {
//           await s3Helper.deleteMultipleFiles(filesToDelete);
//         } catch (deleteError) {
//           console.warn("Error deleting old files:", deleteError);
//         }
//       }

//       const updateData = {
//         ...values,
//         ...attachmentPaths,
//       };
//       delete updateData.jobSummaryAttachment;
//       delete updateData.othersAttachment;

//       const updateResponse = await putApi(
//         `${API_PATHS.POST_RECRUITMENT_PROFILES}/${id}`,
//         updateData
//       );

//       if (updateResponse.status === 200) {
//         if (resetForm) resetForm();
//         toast.success("Job opening updated successfully");
//         dispatch(fetchRecruitmentData());
//         if (navigate) navigate("/recruit/jobProfile");
//         return { ...updateResponse.data, ...attachmentPaths };
//       } else {
//         toast.error(constant.somethingWentWrong);
//         return rejectWithValue("Failed to update job profile");
//       }
//     } catch (error) {
//       console.error("Update Recruitment Profile Error:", error);
//       toast.error(constant.somethingWentWrong);
//       return rejectWithValue(error.message || constant.apiReqFailed);
//     }
//   }
// );

export const updateRecruitmentProfile = createAsyncThunk(
  "recruitment/updateRecruitmentProfile",
  async ({ id, values, resetForm, navigate }, { rejectWithValue, dispatch, getState }) => {
    try {
      console.log("Update values received:", values);

      // Prepare update data - values already contain processed S3 keys from frontend
      const updateData = { ...values };

      console.log("Final update data:", updateData);

      // Update job profile in database
      const updateResponse = await putApi(
        `${API_PATHS.POST_RECRUITMENT_PROFILES}/${id}`,
        updateData
      );

      if (updateResponse.status === 200) {
        if (resetForm) resetForm();
        toast.success("Job opening updated successfully");
        dispatch(fetchRecruitmentData());
        if (navigate) navigate("/recruitment/jobProfile");
        return updateResponse.data;
      } else {
        toast.error(constant.somethingWentWrong);
        return rejectWithValue("Failed to update job profile");
      }
    } catch (error) {
      console.error("Update Recruitment Profile Error:", error);
      toast.error(constant.somethingWentWrong);
      return rejectWithValue(error.message || constant.apiReqFailed);
    }
  }
);
// NEW: Delete attachment from job profile
export const deleteAttachment = createAsyncThunk(
  "recruitment/deleteAttachment",
  async ({ jobId, attachmentType }, { rejectWithValue, dispatch }) => {
    try {
      // Get current job data
      const currentJobResponse = await getApi(`${API_PATHS.RECRUITMENT_PROFILES}${jobId}`);
      const currentJob = currentJobResponse.data;

      const attachmentUrl = currentJob[attachmentType];
      if (!attachmentUrl) {
        return rejectWithValue("Attachment not found");
      }

      const s3Config = getS3Config();
      // validateS3Config(s3Config);;
      const s3Helper = new S3Helper(s3Config);

      // Delete from S3
      const s3Key = extractS3KeyFromUrl(attachmentUrl);
      if (s3Key) {
        try {
          await s3Helper.deleteFile(s3Key);
        } catch (s3Error) {
          console.warn('Failed to delete from S3:', s3Error);
          // Continue with database update even if S3 deletion fails
        }
      }

      // Update database to remove attachment URL
      const updateData = {
        [attachmentType]: null
      };

      const updateResponse = await putApi(
        `${API_PATHS.POST_RECRUITMENT_PROFILES}/${jobId}`,
        updateData
      );

      if (updateResponse.status === 200) {
        toast.success("Attachment deleted successfully");
        dispatch(fetchRecruitmentData());
        return { jobId, attachmentType };
      } else {
        toast.error(constant.somethingWentWrong);
        return rejectWithValue("Failed to delete attachment");
      }

    } catch (error) {
      console.error('Delete Attachment Error:', error);
      toast.error(constant.somethingWentWrong);
      return rejectWithValue(error.message || constant.apiReqFailed);
    }
  }
);

// NEW: Download attachment
export const downloadAttachment = createAsyncThunk(
  "recruitment/downloadAttachment",
  async ({ attachmentUrl, filename }, { rejectWithValue }) => {
    try {
      const s3Config = getS3Config();
      // validateS3Config(s3Config);;
      const s3Helper = new S3Helper(s3Config);

      const s3Key = extractS3KeyFromUrl(attachmentUrl);
      console.log("s3Key", s3Key)
      if (!s3Key) {
        return rejectWithValue("Invalid attachment URL");
      }

      const result = await s3Helper.downloadFile(s3Key, {
        downloadAsBlob: true,
        saveAs: filename
      });

      console.log("download result", result)

      if (result.success) {
        toast.success("File downloaded successfully");
        return result;
      } else {
        toast.error("Failed to download file 1");
        return rejectWithValue("Download failed");
      }

    } catch (error) {
      console.error('Download Attachment Error:', error);
      toast.error("Failed to download file 2");
      return rejectWithValue(error.message || "Download failed");
    }
  }
);

const recruitmentSlice = createSlice({
  name: "recruitment",
  initialState: {
    data: [],
    loading: false,
    error: null,
    addLoading: false,
    updateLoading: false,
    deleteLoading: false,
    downloadLoading: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchRecruitmentData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecruitmentData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchRecruitmentData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(addRecruitmentProfile.pending, (state) => {
        state.addLoading = true;
      })
      .addCase(addRecruitmentProfile.fulfilled, (state) => {
        state.addLoading = false;
      })
      .addCase(addRecruitmentProfile.rejected, (state, action) => {
        state.addLoading = false;
        state.error = action.payload;
      })

      .addCase(updateRecruitmentProfile.pending, (state) => {
        state.updateLoading = true;
      })
      .addCase(updateRecruitmentProfile.fulfilled, (state) => {
        state.updateLoading = false;
      })
      .addCase(updateRecruitmentProfile.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
      })

      .addCase(deleteAttachment.pending, (state) => {
        state.deleteLoading = true;
      })
      .addCase(deleteAttachment.fulfilled, (state) => {
        state.deleteLoading = false;
      })
      .addCase(deleteAttachment.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      })

      .addCase(downloadAttachment.pending, (state) => {
        state.downloadLoading = true;
      })
      .addCase(downloadAttachment.fulfilled, (state) => {
        state.downloadLoading = false;
      })
      .addCase(downloadAttachment.rejected, (state, action) => {
        state.downloadLoading = false;
        state.error = action.payload;
      })


      .addCase(deleteJobProfile.pending, (state) => {
        state.deleteLoading = true
      })
      .addCase(deleteJobProfile.fulfilled, (state, action) => {
        state.deleteLoading = false
        state.data = state.data.filter((profile) => profile._id !== action.payload)
      })
      .addCase(deleteJobProfile.rejected, (state, action) => {
        state.deleteLoading = false
        state.error = action.payload
      })

      .addCase(deleteMultipleJobProfiles.pending, (state) => {
        state.deleteLoading = true
      })
      .addCase(deleteMultipleJobProfiles.fulfilled, (state, action) => {
        state.deleteLoading = false
        state.data = state.data.filter((profile) => !action.payload.includes(profile._id))
      })
      .addCase(deleteMultipleJobProfiles.rejected, (state, action) => {
        state.deleteLoading = false
        state.error = action.payload
      });



  },
});

export default recruitmentSlice.reducer;